import { DatetimeString } from './utils'

export type Occupation = {
  code: string
  name: string
  shortName: string
}

export type User = {
  id: number
  heartlineId: string
  lastName: string
  firstName: string
  lastNameReading: string
  firstNameReading: string
  isGroupContract: boolean
  isOnline?: boolean
}

export type Institution = {
  patient: string
  institution: string
  institutionName: string
  patientLocalId: string
}

export type Contact = {
  id: number
  patient: string
  contactName: string
  relationship: string
  phoneNumber: string
}

export type Patient = User & {
  workerIds: number[]
  birthDate: string
  email: string
  age: number
  status: string
  freeComment: string
  gender: string
  genderDisplay: string
  allergy: string
  institutions: Institution[]
  contacts: Contact[]
  isEnabled: boolean
  medicationNote: string
}

export type Worker = User & {
  institution: string
  institutionName: string
  licenseRegistrationYear: number
  department: string
  occupation: Occupation
  staffNumber: number | null
}

export type WatchingPatient = {
  isAdmin: boolean
  isEnabled: boolean
  patient: number
  worker: number
}

export type PrivateWorker = Omit<Worker, 'occupation'> & {
  postCode: string
  email: string
  addressPrefCode: string
  addressCity: string
  addressStreet: string
  addressEtc: string
  phoneNumber: string
  institution2Code: string
  institution2Name: string
  institution3Code: string
  institution3Name: string
  occupation: string
  watching: Array<WatchingPatient>
}

export type PatientContact = {
  contactName: string
  phoneNumber: string
  relationship: string
}

export type CareLevel =
  | 'なし'
  | '要支援1'
  | '要支援2'
  | '要介護1'
  | '要介護2'
  | '要介護3'
  | '要介護4'
  | '要介護5'

export type PrivatePatient = Patient & {
  postCode: string | null
  email: string
  addressPrefCode: string
  addressCity: string
  addressStreet: string
  addressEtc: string
  phoneNumber: string
  medInsurerName: string
  medInsurerNumber: string
  medInsuredNumber: string
  careInsurerName: string
  careInsurerNumber: string
  careInsuredNumber: string
  hasAllergy: boolean
  careLevel: CareLevel
  institution1Name: string
  institution2Name: string
  institution3Name: string
  institution1PatientId: string
  institution2PatientId: string
  institution3PatientId: string
  careservice1Name: string
  careservice2Name: string
  careservice3Name: string
  careservice1ContractId: string
  careservice2ContractId: string
  careservice3ContractId: string
  freeComment: string
  contacts: Array<PatientContact>
}

export type ExternalService = {
  name: string
  apiBaseUrl: string
  method: string
}

export type LoginUser = User & {
  isWorker: boolean
  isPatient: boolean
  isPatientRelative: boolean
  requireChangePassword: boolean
  videoApiSecret: string
  externalServices: ExternalService[]
}

export const isWorker = (item: any): item is Worker => {
  return !!('occupation' in item && item.occupation)
}
export const isRelative = (item: any): item is Relative => {
  return !!('isPatientRelative' in item && item.isPatientRelative)
}

export type EventWorker = {
  isRead: boolean
  readDatetime: DatetimeString
  worker: Worker | number
}

export type Relative = User & {
  email: string
  isActive: boolean
  relativeInvitationUrl?: string
  isOnline: boolean
  isPatientRelative: boolean
}

export type PatientInRelative = Pick<
  Patient,
  | 'id'
  | 'heartlineId'
  | 'lastName'
  | 'firstName'
  | 'lastNameReading'
  | 'firstNameReading'
  | 'age'
> & { isOnline: boolean }
export type PrivateRelative = Omit<User, 'isGroupContract' | 'isOnline'> & {
  age: string
  email: string
  postCode: string
  addressPrefCode: string
  addressCity: string
  addressStreet: string
  addressEtc: string
  phoneNumber: string
  gender: string
  birthDate: string
  patient: PatientInRelative
}
